import { Body2, UIHeading5, Image } from '@cian/ui-kit';
import { IconFileDocument16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { clsx } from 'clsx';

import type { TThumbnail } from '../../types/thumbnail';

import styles from './MessageReplyQuote.css';

type TMessageReplyQuoteProps = {
  authorName: string;
  text: string;
  thumbnail?: TThumbnail;
  isOutcoming?: boolean;
  onClick: VoidFunction;
};

export const MessageReplyQuote: React.FC<TMessageReplyQuoteProps> = React.memo(function MessageReplyQuote({
  authorName,
  text,
  thumbnail,
  isOutcoming,
  onClick,
}) {
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <div
      className={clsx(styles['container'], !isOutcoming && styles['container--incoming'])}
      data-name="MessageReplyQuote"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={styles['border']} />

      {thumbnail?.type === 'document' && (
        <div className={styles['icon-wrapper']}>
          <IconFileDocument16 color="icon-secondary-default" />
        </div>
      )}

      {thumbnail?.type === 'image' && (
        <div className={styles['thumbnail-wrapper']}>
          <Image
            src={thumbnail.url}
            width={32}
            height={32}
            objectFit="cover"
            borderRadius="4px"
            preloader={false}
            alt=""
          />
        </div>
      )}

      <div className={styles['text']}>
        <div className={styles['author']}>
          <UIHeading5 fontWeight="700" ellipsis color="current_color">
            {authorName}
          </UIHeading5>
        </div>
        <div className={styles['body']}>
          <Body2 ellipsis color="current_color">
            {text}
          </Body2>
        </div>
      </div>
    </div>
  );
});
